import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { Button, Switch } from '../../../components/ui';
import {
  DISPLAYED_MODULE_GROUP_KEYS,
  MODULE_GROUP_DESCRIPTIONS,
  MODULE_GROUP_LABELS,
  ModuleEntitlements,
  ModuleGroupKey,
  isAlwaysOnModuleGroup,
} from './config';
import { useModuleEntitlements } from './useData';

const PackCard: React.FC<{
  packKey: ModuleGroupKey;
  enabled: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}> = ({ packKey, enabled, disabled, onChange }) => {
  const { colors } = useTheme();
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 rounded-2xl border px-5 py-4',
        colors.border,
        colors.surface,
      )}
    >
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-semibold', colors.text)}>{MODULE_GROUP_LABELS[packKey]}</p>
        <p className={cn('mt-1 text-sm leading-relaxed', colors.textMuted)}>
          {MODULE_GROUP_DESCRIPTIONS[packKey]}
        </p>
      </div>
      <Switch
        checked={enabled}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={`Enable ${MODULE_GROUP_LABELS[packKey]}`}
      />
    </div>
  );
};

export const ModuleManagementPage: React.FC = () => {
  const { colors } = useTheme();
  const { showNotification } = useNotification();
  const { entitlements, isLoading, updateEntitlements } = useModuleEntitlements();
  const [draft, setDraft] = useState<ModuleEntitlements>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entitlements && Object.keys(entitlements).length > 0) {
      setDraft({ ...entitlements });
    }
  }, [entitlements]);

  const dirty = useMemo(() => {
    return DISPLAYED_MODULE_GROUP_KEYS.some((key) => (draft[key] !== false) !== (entitlements[key] !== false));
  }, [draft, entitlements]);

  const setPack = (key: ModuleGroupKey, enabled: boolean) => {
    if (isAlwaysOnModuleGroup(key)) return;
    setDraft((prev) => ({ ...prev, [key]: enabled }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, boolean> = {};
      for (const key of DISPLAYED_MODULE_GROUP_KEYS) {
        payload[key] = draft[key] !== false;
      }
      await updateEntitlements(payload);
      showNotification('Module entitlements saved', 'success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save entitlements';
      showNotification(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft({ ...entitlements });
  };

  const enableAll = () => {
    const next = { ...draft };
    for (const key of DISPLAYED_MODULE_GROUP_KEYS) next[key] = true;
    setDraft(next);
  };

  const disableAll = () => {
    const next = { ...draft };
    for (const key of DISPLAYED_MODULE_GROUP_KEYS) next[key] = false;
    setDraft(next);
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col', colors.background)}>
      <div className={cn('border-b px-6 py-5', colors.border, colors.surface)}>
        <h1 className={cn('text-xl font-semibold', colors.text)}>Module management</h1>
        <p className={cn('mt-1 max-w-2xl text-sm leading-relaxed', colors.textMuted)}>
          Enable or disable licensed module packs for this company. Disabled packs are hidden from the
          menu and blocked by deep link. Core reference data (vessels, ports, currencies, …) and System
          stay available because other packs depend on them.
        </p>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {isLoading && Object.keys(draft).length === 0 ? (
          <p className={cn('text-sm', colors.textMuted)}>Loading entitlements…</p>
        ) : (
          <div className="mx-auto flex max-w-3xl flex-col gap-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={enableAll}>
                Enable all
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={disableAll}>
                Disable all
              </Button>
            </div>

            {DISPLAYED_MODULE_GROUP_KEYS.map((key) => (
              <PackCard
                key={key}
                packKey={key}
                enabled={draft[key] !== false}
                onChange={(next) => setPack(key, next)}
              />
            ))}

            <div className={cn('mt-4 rounded-2xl border border-dashed px-5 py-4', colors.border)}>
              <p className={cn('text-sm font-semibold', colors.text)}>Always available</p>
              <p className={cn('mt-1 text-sm', colors.textMuted)}>
                <span className="font-medium">Core reference data</span> — vessels, ports, entities,
                countries, currencies, departments.
                <br />
                <span className="font-medium">System</span> — users, application settings, this page, and
                tools.
              </p>
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          'flex items-center justify-end gap-3 border-t px-6 py-4',
          colors.border,
          colors.surface,
        )}
      >
        <Button type="button" variant="secondary" disabled={!dirty || saving} onClick={handleReset}>
          Reset
        </Button>
        <Button type="button" disabled={!dirty || saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
};

export default ModuleManagementPage;
